import { FontIcon, getTheme, ITheme } from '@fluentui/react';
import AppContext, { IAppContext } from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import SupportTicketModal from 'components/Dialogs/SupportTicketModal';
import { freshdeskWidgetOpen, freshdeskWidgetSetContactInfo, freshdeskWidgetSetTranslations } from 'globalFunctions';
import {
  globalBoxBorderRadius,
  globalFontBoldWeight,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalStackTokensGapXLarge,
  globalTextStylesBold,
} from 'globalStyles';
import Language from 'models/language';
import Package from 'models/package';
import { TenantOnboarding, TrialWelcomeIntent } from 'models/tenant';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import { RenderDemo } from './TrialWelcomeDemos';
import { RenderDone, RenderQuickActionButtons, RenderQuickLinks } from './TrialWelcomeNextStepActions';

interface ITrialWelcomeNextStepsProps {
  onboardingState: TenantOnboarding;
  updateOnboardingState: (onboardingState: TenantOnboarding) => Promise<void>;
  packages: Package[];
  updatePackage: (updatedPackage: Package) => void;
  selectedLang: Language | undefined;
}

interface IDemoCard {
  id: string;
  stepNr: number;
  title: string;
  subText: string;
}

export const getTrialWelcomePanelChrome = (appContext: IAppContext, theme: ITheme) => {
  return {
    background: appContext.useDarkMode ? '#202a33' : '#f5f9fd',
    border: `1px solid ${appContext.useDarkMode ? '#385163' : theme.palette.themeLight}`,
    borderLeft: `4px solid ${theme.palette.themePrimary}`,
    boxShadow: appContext.useDarkMode ? '0 1px 3px rgba(0, 0, 0, 0.28)' : '0 1px 3px rgba(0, 90, 158, 0.14)',
  };
};

const TrialWelcomeNextSteps = (props: ITrialWelcomeNextStepsProps) => {
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany']);
  const appContext = useContext(AppContext);
  const [showTicketModal, setShowTicketModal] = useState<boolean>(false);
  const theme = getTheme();

  const demoCardSets: Record<TrialWelcomeIntent, IDemoCard[]> = {
    [TrialWelcomeIntent.IsFirstISO]: [
      {
        id: 'cmpf6t7la000eyf0jxayvofih',
        stepNr: 1,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Overview.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Overview.SubText'),
      },
      {
        id: 'cmn4gq76217p3z3qmwj1kh8yx',
        stepNr: 2,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Risks.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Risks.SubText'),
      },
      {
        id: 'cmnd8nsxx244axtgux1pmguhg',
        stepNr: 3,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Tasks.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Tasks.SubText'),
      },
      {
        id: 'cmosjxtzn03vwvp0jlsjk3lgc',
        stepNr: 4,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Requirements.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.FirstISO.Requirements.SubText'),
      },
    ],
    [TrialWelcomeIntent.IsCertified]: [
      {
        id: 'cmpf6td7r01nzyt0jcr2asmp7',
        stepNr: 1,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Overview.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Overview.SubText'),
      },
      {
        id: 'cmn4gq76217p3z3qmwj1kh8yx',
        stepNr: 2,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Risks.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Risks.SubText'),
      },
      {
        id: 'cmnd8nsxx244axtgux1pmguhg',
        stepNr: 3,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Tasks.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Tasks.SubText'),
      },
      {
        id: 'cmosjxtzn03vwvp0jlsjk3lgc',
        stepNr: 4,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Requirements.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Certified.Requirements.SubText'),
      },
    ],
    [TrialWelcomeIntent.IsConsultant]: [
      {
        id: 'cmpf6td7r01nzyt0jcr2asmp7',
        stepNr: 1,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Overview.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Overview.SubText'),
      },
      {
        id: 'cmn4gq76217p3z3qmwj1kh8yx',
        stepNr: 2,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Risks.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Risks.SubText'),
      },
      {
        id: 'cmnd8nsxx244axtgux1pmguhg',
        stepNr: 3,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Tasks.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Tasks.SubText'),
      },
      {
        id: 'cmosjxtzn03vwvp0jlsjk3lgc',
        stepNr: 4,
        title: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Requirements.Title'),
        subText: t('dashboard:TrialWelcome.NextSteps.Demos.Consultant.Requirements.SubText'),
      },
    ],
  };

  const demoCards = demoCardSets[props.onboardingState.onboardingSteps.intent || TrialWelcomeIntent.IsFirstISO];

  const openTicket = () => {
    if (Config.isHubSpotSupportEnabled()) {
      setShowTicketModal(true);
    } else {
      freshdeskWidgetOpen();
      freshdeskWidgetSetContactInfo(appContext.user.name, appContext.user.email);
      freshdeskWidgetSetTranslations();
    }
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill styles={{ root: { paddingRight: 20 } }}>
      <IPStack.Item>
        {hasUserFeatureGenericManager(appContext) && (
          <IPStack.Item styles={{ root: { paddingBottom: 20 } }}>
            <RenderDone onboardingState={props.onboardingState} updateOnboardingState={props.updateOnboardingState} />
          </IPStack.Item>
        )}
        <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapXLarge}>
          <IPStack tokens={globalStackTokensGapSmall} horizontalAlign="center">
            <IPStack.Item>
              <IPText block variant="xxLarge" styles={globalTextStylesBold}>
                {t('dashboard:TrialWelcome.NextSteps.Title')}
              </IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPText block variant="mediumPlus">
                {t('dashboard:TrialWelcome.NextSteps.SubTitle')}
              </IPText>
            </IPStack.Item>
          </IPStack>
          <IPStack.Item styles={{ root: { width: '100%' } }}>
            <IPStack
              tokens={globalStackTokensGapMedium}
              styles={{
                root: {
                  ...getTrialWelcomePanelChrome(appContext, theme),
                  borderRadius: globalBoxBorderRadius,
                  paddingBottom: 30,
                  paddingLeft: 30,
                  paddingRight: 30,
                  paddingTop: 30,
                  width: '100%',
                },
              }}
            >
              <IPStack.Item>
                <IPText
                  block
                  variant="small"
                  styles={{
                    root: {
                      color: theme.palette.neutralSecondary,
                      fontWeight: globalFontBoldWeight,
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  {t('dashboard:TrialWelcome.NextSteps.GuideTag')}
                </IPText>
              </IPStack.Item>

              <IPStack.Item>
                <IPStack horizontal wrap verticalAlign="center" tokens={{ childrenGap: '24 0' }}>
                  {demoCards.map((demoCard, index) => (
                    <IPStack horizontal verticalAlign="center" key={demoCard.stepNr}>
                      <IPStack.Item>
                        <RenderDemo
                          id={demoCard.id}
                          stepNr={demoCard.stepNr}
                          title={demoCard.title}
                          subText={demoCard.subText}
                        />
                      </IPStack.Item>
                      {index < demoCards.length - 1 && (
                        <IPStack.Item
                          styles={{
                            root: {
                              alignItems: 'center',
                              display: 'flex',
                              justifyContent: 'center',
                              width: 30,
                            },
                          }}
                        >
                          <FontIcon
                            iconName="ChevronRight"
                            style={{ color: theme.palette.neutralTertiary, fontSize: 20 }}
                          />
                        </IPStack.Item>
                      )}
                    </IPStack>
                  ))}
                </IPStack>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { width: '100%' } }}>
            <RenderQuickActionButtons onboardingSteps={props.onboardingState.onboardingSteps} />
          </IPStack.Item>
          <IPStack.Item styles={{ root: { width: '100%' } }}>
            <RenderQuickLinks onContactServiceDesk={openTicket} />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <SupportTicketModal
        isOpen={showTicketModal}
        onDismiss={() => setShowTicketModal(false)}
        initialSubject={t('dashboard:TrialWelcome.NextSteps.Info.LinkTicketSubject')}
      />
    </IPStack>
  );
};

export default TrialWelcomeNextSteps;
